import { useEffect, useRef, useState } from "react";
import { setAppTheme } from "../../shared/Settings/appTheme";
import type { tbEvent, ViewEventData, TbRegistration, EventGroup, MomentUser, PaceOfPlayRow, EventGroupType } from "@moment-golf/types";

const emptyViewEventData: ViewEventData = {
    registrations: [],
    scores: [],
    courseData: [],
};

export function useTBStates() {

    const [userData, setUserData] = useState<unknown>(null);
    const [selectedUser, setSelectedUser] = useState<MomentUser | null>(null);

    const [appSection, setAppSection] = useState('tournaments');
    const [viewPanel, setViewPanel] = useState('snapshot');
    const [groupTypeFilter, setGroupTypeFilter] = useState<EventGroupType | 'all'>('all');
    const [factor, setFactor] = useState(1);
    const [appTheme, setTheme] = useState(setAppTheme(factor));

    const [eventData, setEventData] = useState<tbEvent[]>([]);
    const [viewEvent, setViewEvent] = useState<tbEvent | null>(null);
    const [viewEventData, setViewEventData] = useState<ViewEventData>(emptyViewEventData);

    const [editMode, setEditMode] = useState(false);
    const [eventTab, setEventTab] = useState('details');
    const [eventTabValue, setEventTabValue] = useState(0);
    const [roundTab, setRoundTab] = useState('details');
    const [roundTabValue, setRoundTabValue] = useState(0);
    const [roundValue, setRoundValue] = useState(1);

    const [groupData, setGroupData] = useState<EventGroup[]>([]);
    const [viewGroup, setViewGroup] = useState<EventGroup | null>(null);
    const [groupTabValue, setGroupTabValue] = useState(0);

    const [optionsLoaded, setOptionsLoaded] = useState<unknown[]>([]);

    const [forceUpdate, setForceUpdate] = useState(false);

    const [paceData, setPaceData] = useState<PaceOfPlayRow[]>([]);
    const [paceDataLastFetched, setPaceDataLastFetched] = useState<string | null>(null);

    const originalViewEvent = useRef<tbEvent | null>(JSON.parse(JSON.stringify(viewEvent ?? null)));
    const originalViewEventData = useRef<ViewEventData>(JSON.parse(JSON.stringify(viewEventData)));
    const originalRegistrations = useRef<TbRegistration[]>(JSON.parse(JSON.stringify(viewEventData.registrations)));
    const originalScores = useRef<unknown[]>([]);

    useEffect(() => {
        if (viewEvent) {
            const hdcpArr = Array.isArray(viewEvent.hdcpUsage) ? viewEvent.hdcpUsage : (viewEvent.hdcpUsage ? [viewEvent.hdcpUsage] : []);
            // Sets a default hdcpUsage if it is not already set and is needed
            if (viewEvent.scoreFormat !== 'Gross' && hdcpArr.length === 0) {
                setViewEvent((prevState: any) => ({
                    ...prevState,
                    hdcpUsage: [optionsLoaded
                        .find((o: any) => o.keyName === 'HdcpTypes').options
                        .find((o: any) => o.type === 'Straight').defaultUsage]
                }));
            }

            // Sets hdcpUsage to an empty array if the score format is now Gross and hdcpUsage is set
            if (viewEvent.scoreFormat === 'Gross' && hdcpArr.length > 0) {
                setViewEvent({
                    ...viewEvent,
                    hdcpUsage: []
                });
            }
        }
    }, [viewEvent?.scoreFormat]);

    useEffect(() => {
        console.log('Round Score Format Changed');
        if (viewEvent && viewEvent.rounds[roundValue-1]) {
            const roundHdcp = viewEvent.rounds[roundValue-1].hdcpUsage;
            const roundHdcpArr = Array.isArray(roundHdcp) ? roundHdcp : (roundHdcp ? [roundHdcp] : []);
            // Sets a default hdcpUsage if it is not already set and is needed
            if (viewEvent.rounds[roundValue-1].scoreFormat !== 'Gross' && roundHdcpArr.length === 0) {
                const eventHdcp = Array.isArray(viewEvent.hdcpUsage) ? viewEvent.hdcpUsage : (viewEvent.hdcpUsage ? [viewEvent.hdcpUsage] : []);
                const defaultUsage = optionsLoaded?.find((o: any) => o.keyName === 'HdcpTypes')?.options?.find((o: any) => o.type === 'Straight')?.defaultUsage;
                const roundsHdcp = eventHdcp.length > 0 ? eventHdcp : (defaultUsage ? [defaultUsage] : []);
                setViewEvent((prevState: any) => ({
                    ...prevState,
                    rounds: prevState.rounds.map((round: any) => round.roundNumber === roundValue ? {
                        ...round,
                        hdcpUsage: roundsHdcp
                    } : round)
                }));
            }

            // Sets hdcpUsage to an empty array if the score format is now Gross and hdcpUsage is set
            if (viewEvent.rounds[roundValue-1].scoreFormat === 'Gross' && roundHdcpArr.length > 0) {
                setViewEvent((prevState: any) => ({
                    ...prevState,   
                    rounds: prevState.rounds.map((round: any) => round.roundNumber === roundValue ? {
                        ...round,
                        hdcpUsage: []
                    } : round)
                }));
            }
        }
    }, [viewEvent?.rounds[roundValue-1]?.scoreFormat]);

    return {
        appSection, setAppSection,
        viewPanel, setViewPanel,
        groupTypeFilter, setGroupTypeFilter,
        factor, setFactor,
        appTheme, setTheme,
        eventData, setEventData,
        viewEvent, setViewEvent,
        viewEventData, setViewEventData,
        editMode, setEditMode,
        eventTab, setEventTab,
        eventTabValue, setEventTabValue,
        roundTab, setRoundTab,
        roundTabValue, setRoundTabValue,
        roundValue, setRoundValue,
        groupData, setGroupData,
        viewGroup, setViewGroup,
        groupTabValue, setGroupTabValue,
        optionsLoaded, setOptionsLoaded,
        forceUpdate, setForceUpdate,
        userData, setUserData,
        selectedUser, setSelectedUser,
        originalViewEvent,
        originalViewEventData,
        originalRegistrations,
        originalScores,
        paceData, setPaceData,
        paceDataLastFetched, setPaceDataLastFetched,
    }
}